import {
  AiReviewFlag,
  AiReviewItem,
  AiReviewReason,
  EAiReviewStatus
} from "../aiReview.types";
import { AI_REVIEW, AI_REVIEW_DETAILS } from "../constants/review";

// Pure helpers of the "To review" page (relative imports only), so they can
// be unit tested.

export type AiReviewTab = "open" | "reviewed" | "dismissed";

export const AI_REVIEW_TABS: readonly AiReviewTab[] = [
  "open",
  "reviewed",
  "dismissed"
];

export const reviewTabStatus = (tab: AiReviewTab): EAiReviewStatus => {
  switch (tab) {
    case "reviewed":
      return EAiReviewStatus.Reviewed;
    case "dismissed":
      return EAiReviewStatus.Dismissed;
    default:
      return EAiReviewStatus.Open;
  }
};

export type AiReviewScoreLevel = "high" | "medium" | "low";

// The colour of the score badge (`aiReview.score.<level>` as its title).
export const reviewScoreLevel = (score: number): AiReviewScoreLevel => {
  if (score >= AI_REVIEW.SCORE_LEVELS.HIGH) return "high";
  if (score >= AI_REVIEW.SCORE_LEVELS.MEDIUM) return "medium";
  return "low";
};

const AI_REVIEW_FLAGS = Object.keys(AI_REVIEW_DETAILS) as AiReviewFlag[];

const isReviewFlag = (code: unknown): code is AiReviewFlag =>
  typeof code === "string" && (AI_REVIEW_FLAGS as string[]).includes(code);

export interface AiReviewReasonKeys {
  title: string;
  details: string[];
}

// The i18n keys of one reason: its chip label and the lines of its tooltip.
// Unknown sub-codes are left out; an unknown code gives null (no chip), so a
// newer backend with a tenth flag does not show a raw key.
export const reviewReasonKeys = (
  reason: Pick<AiReviewReason, "code" | "details">
): AiReviewReasonKeys | null => {
  if (!isReviewFlag(reason.code)) return null;
  const code = reason.code;
  const known = AI_REVIEW_DETAILS[code];
  const details = Array.isArray(reason.details) ? reason.details : [];
  return {
    title: `aiReview.flag.${code}`,
    details: [...new Set(details)]
      .filter((detail) => known.includes(detail))
      .map((detail) => `aiReview.detail.${code}.${detail}`)
  };
};

// The reason was seen in the customer's next message (a correction, a
// repeated message): the reply at fault is still this item's.
export const reviewSeenLater = (
  item: Pick<AiReviewItem, "turnMessageId">,
  reason: Pick<AiReviewReason, "seenInTurnMessageId">
): boolean =>
  Boolean(reason.seenInTurnMessageId) &&
  reason.seenInTurnMessageId !== item.turnMessageId;

export type AiReviewAction =
  | "open"
  | "createLesson"
  | "markReviewed"
  | "dismiss"
  | "reopen";

// Everyone may open the conversation; handling an item needs the "Manage AI
// lessons" permission (a display gate, the backend checks it again).
export const reviewActionsFor = (
  item: Pick<AiReviewItem, "status">,
  canManage: boolean
): AiReviewAction[] => {
  if (!canManage) return ["open"];
  return item.status === EAiReviewStatus.Open
    ? ["open", "createLesson", "markReviewed", "dismiss"]
    : ["open", "reopen"];
};

// The toast after a status change (V3).
export const reviewStatusToastKey = (status: EAiReviewStatus): string => {
  switch (status) {
    case EAiReviewStatus.Reviewed:
      return "aiReview.toast.reviewed";
    case EAiReviewStatus.Dismissed:
      return "aiReview.toast.dismissed";
    default:
      return "aiReview.toast.reopened";
  }
};

const AI_CONVERSATIONS_PATH = "/ai/conversations";

export const reviewConversationPath = (
  item: Pick<AiReviewItem, "conversationId">
): string =>
  `${AI_CONVERSATIONS_PATH}?${new URLSearchParams({
    conversationId: item.conversationId
  }).toString()}`;

// The conversations page opens the correction panel on the turn's reply.
export const reviewCorrectionPath = (
  item: Pick<AiReviewItem, "conversationId" | "turnMessageId">
): string =>
  `${AI_CONVERSATIONS_PATH}?${new URLSearchParams({
    conversationId: item.conversationId,
    turnMessageId: item.turnMessageId
  }).toString()}`;

const AI_TICKETS_PATH = "/ai/tickets";

// A ticket in one of these is done: the others are still being handled.
const HANDLED_TICKET_STATUSES: ReadonlySet<string> = new Set([
  "resolved",
  "closed"
]);

export interface AiReviewTicketBadge {
  // The ticket, opened on the AI tickets page (`?ticketId=`).
  path: string;
  // The badge's tooltip (the ticket is in progress, or done).
  tooltipKey: string;
  handled: boolean;
}

// "Ticket already open": an AI ticket already covers this reply, so the team
// does not handle the same problem twice. Null without a ticket.
export const reviewTicketBadge = (
  item: Pick<AiReviewItem, "ticket">
): AiReviewTicketBadge | null => {
  const ticket = item.ticket;
  if (!ticket || typeof ticket.id !== "string" || !ticket.id) return null;
  const handled = HANDLED_TICKET_STATUSES.has(ticket.status);
  return {
    path: `${AI_TICKETS_PATH}?${new URLSearchParams({
      ticketId: ticket.id
    }).toString()}`,
    tooltipKey: handled
      ? "aiReview.ticket.handledHint"
      : "aiReview.ticket.openHint",
    handled
  };
};

// A voice note or a photo has no words to quote: a label is shown instead of
// the excerpt.
export const reviewInboundLabelKey = (
  item: Pick<AiReviewItem, "inboundType">
): string | null => {
  switch (item.inboundType) {
    case "voice":
      return "aiReview.voiceNote";
    case "image":
      return "aiReview.photo";
    default:
      return null;
  }
};
